/**
 * runSeedById — resolve a SeedProvider by id and apply its payload to the
 * canvas via the CommandManager. Shared by `useStartIntentSeed` (deep-link /
 * agent path) and the in-editor Start pickers. Returns true if it seeded.
 */

import type { CommandManager } from '../commands/CommandManager';
import type { SeedBuildContext } from './SeedRegistry';
import { getSeedProvider } from './SeedRegistry';
import { registerDefaultSeeds } from './registerDefaultSeeds';

export async function runSeedById(
  cm: CommandManager,
  seedId: string,
  ctx?: SeedBuildContext,
): Promise<boolean> {
  registerDefaultSeeds();
  const provider = getSeedProvider(seedId);
  if (!provider) {
    console.debug('[seed] no provider for', seedId);
    return false;
  }
  const payload = await provider.provide();

  if (payload.format === 'rdm') {
    return applyRdmText(cm, payload.rdm, payload.layoutOptions);
  }

  if (payload.format === 'commands') {
    await payload.build(cm, ctx);
    return true;
  }

  // 'clone' → applied server-side at create time.
  return false;
}

/**
 * Materialize an RDM document onto the canvas through the EXACT production
 * NL→diagram stack (rdmApplyTool / Toolbar RDM import): parseAndValidate →
 * toLayoutedRuntimeModel → ApplyRdmSceneCommand. ApplyRdmSceneCommand →
 * replaceSceneInWorkspace writes the pool-table node + lane bindings as ONE
 * coherent scene; BatchCreateNodesCommand (which appended nodes
 * individually) is what broke swimlanes. Shared by `rdm`-format seeds and
 * the agent-prompt seed.
 */
export async function applyRdmText(
  cm: CommandManager,
  rdm: string,
  layoutOptions?: import('../../domains/rdm/services/RdmLayoutService').RdmLayoutOptions,
): Promise<boolean> {
  const [{ rdmService }, { ApplyRdmSceneCommand }] = await Promise.all([
    import('../../domains/rdm/services/RdmService'),
    import('../commands/ApplyRdmSceneCommand'),
  ]);
  const parsed = rdmService.parseAndValidate(rdm);
  if (!parsed.success || !parsed.document) {
    console.warn('[seed] RDM parse failed', parsed.errors);
    return false;
  }
  // `anchorMode: 'redistribute'` — re-pick edge sides from POST-layout
  // geometry. The default ('preserve') keeps the hydrator's first-pass
  // anchors computed against the dumb pre-layout grid, which after ELK
  // moves nodes leaves connectors entering on the wrong side. This matches
  // the production materializers (ProcessDocDiagramMaterializerService,
  // ProcessViewGenerationService) — seeds are a fresh full layout too.
  const { nodes, edges } = await rdmService.toLayoutedRuntimeModel(parsed.document, {
    anchorMode: 'redistribute',
    ...layoutOptions,
  });
  if (nodes.length === 0) return false;
  cm.execute(new ApplyRdmSceneCommand({ nodes, edges }));
  return true;
}
